import { Formik } from "formik";
import * as React from "react";
import * as yup from "yup";
import styled, { css } from "styled-components";
import * as _ from "lodash";
import { toast } from "react-toastify";
import { useDispatch } from "redux-react-hook";

import {
  Form,
  IFormField
} from "src/library/components/Forms";
import Grid from "src/library/components/Hedron";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Layout } from "src/core/shared/components/Layout";
import {
  UpdateUserParams,
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import { useUser } from "src/core/users/hooks";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  IRole,
  useRolesApi
} from "src/core/shared/api/roles.api";
import { Text } from "src/library/components/Text";
import { UserGroupMultiSelect } from "src/core/users/components/UserGroupMultiSelect";
import { setUser } from "src/core/users/store/users.actions";
import { e164PhoneRegex } from "src/utils/validation/regex";

type UrlParams = {
  userId: string;
};

const updateUserForm: (user: IUser, roles: IRole[], canChangeRole?: boolean) => IFormField[] = (user, roles, canChangeRole) => {
  const fields: IFormField[] = [
    {
      type: "input",
      name: "firstName",
      label: "First Name",
      placeholder: "First Name",
      value: user.firstName,
      validation: yup
        .string()
        .min(1)
        .required("First Name is required")
    },
    {
      type: "input",
      name: "lastName",
      label: "Last Name",
      placeholder: "Last Name",
      value: user.lastName,
      validation: yup
        .string()
        .min(1)
        .required("Last Name is required")
    },
    {
      type: "input",
      name: "email",
      label: "Email",
      placeholder: "Email",
      value: user.email,
      disabled: true,
      validation: yup
        .string()
        .email("Please enter a valid email address")
        .required("Email is required")
    },
    {
      type: "input",
      name: "phoneNumber",
      label: "Phone Number",
      placeholder: "Phone Number",
      value: user.phoneNumber,
      validation:
        yup.string().when("mFAMethod", {
          is: "phoneNumber",
          then: yup.string().matches(e164PhoneRegex, "Please enter a valid UK telephone number starting with +44").required("Phone Number is required"),
          otherwise: yup.string().test("optional-phone", "Please enter a valid UK telephone number starting with +44", function (value) {
            if (!value || value.length === 0) {
              return true; // Allow empty values when not required
            }

            return e164PhoneRegex.test(value);
          })
        })
    },
    {
      type: "radio",
      name: "mFAMethod",
      label: "Multi-Factor Authentication",
      value: user.mFAMethod,
      options: [
        {
          text: "Email - code sent by email, requires an email address",
          value: "email",
          checked: user.mFAMethod === "email" ? true : false
        },
        {
          text: "Telephone - code sent by SMS, requires a phone number",
          value: "phoneNumber",
          checked: user.mFAMethod === "phoneNumber" ? true : false
        },
        {
          text: "Disabled - MFA will not be enabled",
          value: "disabled",
          checked: user.mFAMethod === "disabled" ? true : false
        }
      ]
    }
  ];

  if (canChangeRole) {
    fields.push({
      type: "select",
      name: "roleId",
      label: "Role",
      placeholder: "Role",
      value: user.roleId,
      options: _.map(roles, role => ({
        text: `${role.name}`,
        value: role.id.toString()
      })),
      validation: yup
        .string()
        .min(1)
        .required("Role is required")
    });
  }

  return fields;
};

/**
 * Main "EditUser" View
 *
 * @returns
 */
const EditUserPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const { terminology, match } = props;
  const userId = match ? parseInt(match.params.userId, 0) : 0;
  const [ { user, isLoading } ] = useUser(userId);
  const [ rolesResponse, listRoles ] = useRolesApi("LIST_ROLES");
  const [ updateUserResponse, updateUser ] = useUserApi("UPDATE_USER");
  const dispatch = useDispatch();
  const [ selectedUserGroups, setSelectedUserGroups ] = React.useState<number[]>([]);
  const formRef = React.useRef<Formik>(null);
  const checkPermission = useCheckUserPermission();

  React.useEffect(() => {
    listRoles({
      filters: {
        type: "admin",
        code: { $ne: "super-admin" }
      }
    });
  }, [ listRoles ]);

  React.useEffect(() => {
    // On Success reset the form
    if (updateUserResponse.data) {
      dispatch(setUser(updateUserResponse.data));
      props.history.push(`/admin/users/${updateUserResponse.data.id}`);
    } else if (updateUserResponse.error) {
      toast.error(updateUserResponse.error.message);
    }
  }, [
    dispatch,
    props.history,
    updateUserResponse
  ]);

  React.useEffect(() => {
    if (rolesResponse.error) {
      toast.error(rolesResponse.error.message);
    }
  }, [ rolesResponse.error ]);

  React.useEffect(() => {
    if (user?.userGroups && user.userGroups.length > 0) {
      // update initial value
      setSelectedUserGroups(user.userGroups.map(g => g.id));
    }
  }, [ user ]);

  const formFields = React.useMemo(() => {
    if (!user || user.id !== userId) return [];

    return updateUserForm(user, rolesResponse.data || [], checkPermission("amend-role-permissions"));
  }, [
    user,
    rolesResponse.data,
    checkPermission,
    userId
  ]);

  return (
    <Layout {...props}>
      <FormWrapper>
        {rolesResponse.pending || isLoading ? <LoadingSpinner type="circular" /> :
        user && (
          <Form
            key={`form-${user.id}`}
            ref={formRef}
            error={updateUserResponse.error ? updateUserResponse.error.message : null}
            submitting={updateUserResponse.pending}
            fields={formFields}
            onSubmit={userValues => updateUser(Object.assign(_.omit(user, "userGroups"), {
              ...userValues,
              mFAMethod: (userValues.mFAMethod as string),
              userGroupIds: selectedUserGroups.map(groupId => groupId)
            }) as UpdateUserParams)}
            submitButtonText={`Save ${terminology.user.singular.text}`}
          >
            <div className="assigned-user-groups-container">
              <Text as="h3">
                Assigned User Groups
              </Text>

              <Text>
                Manage what groups the admin user is assocaited with.
              </Text>

              <div className="user-group-multi-select">
                <UserGroupMultiSelect
                  labelOverride=" "
                  key="search"
                  onUserGroupsChange={({ fullUserGroupsSelected }) => setSelectedUserGroups(fullUserGroupsSelected.map(ug => ug.id))}
                  initialValues={selectedUserGroups}
                />
              </div>
            </div>
          </Form>
        )}

      </FormWrapper>
    </Layout>
  );
};

const FormWrapper = styled(Grid.Box)`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    justify-content: flex-start;

    form {
      width: 400px;
    }

    .assigned-user-groups-container,
    .user-group-multi-select {
      margin: ${theme.spacing.margins.md} 0;
    }

    .assigned-user-groups-container {
      border-top: 1px solid ${theme.colors.lightGrey};
      padding-top: ${theme.spacing.padding.md};
      margin-top: ${theme.spacing.margins.lg};
    }
  `}
`;

export default EditUserPage;
